import { ReactNode } from 'react';
import * as R from 'remeda';
import humanizeDuration from 'humanize-duration';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { connect } from 'react-redux';
import { ValuesBase } from '@rossum/api-client/annotations';
import { AggregateStat } from '@rossum/components/AggregateStat';
import {
  IconBolt,
  IconCancel,
  IconCheck,
  IconFileTime,
  IconHandFinger,
  IconHourglassEmpty,
  IconHourglassHigh,
  IconLogin,
  IconLogout,
  IconPencil,
  IconTrash,
} from '@rossum/ui/icons/tabler';
import { Stack, SvgIcon } from '@rossum/ui/material';
import { humanizeDurationLocaleMap, LocalizationKeys } from '../../../i18n';
import { fallbackLocale } from '../../../i18n/config';
import { getDurationUnit, limitUnits } from '../../../lib/timeUtils';

// TODO:
//   - unify type with props type of AggregateState compoennt
//   - replace ReactNode types with string / number
//   - replace formatted message components with inttl.formatMessage functions
export type AggregateData = Array<{
  value: ReactNode;
  icon: ReactNode;
  description: LocalizationKeys;
}>;

type AggregateProps = {
  data: AggregateData;
};

type Props = {
  isTmpState: boolean;
  totals: ValuesBase;
};

type IntlProps = { intl: IntlShape };
type PropsWithIntl = Props & IntlProps;

export const Aggregate = ({ data }: AggregateProps) => (
  <Stack
    direction="row"
    alignItems="center"
    pl={3}
    justifyContent="space-around"
  >
    {data.map(({ value, icon, description }) => (
      <AggregateStat
        key={description}
        icon={icon}
        value={value}
        description={<FormattedMessage id={description} />}
      />
    ))}
  </Stack>
);

export const UsageAggregate = connect(
  (_, { isTmpState, totals }: Props): AggregateProps => ({
    data: [
      {
        value: totals.importedCount,
        icon: (
          <SvgIcon fontSize="large" color={isTmpState ? 'action' : 'info'}>
            <IconLogin />
          </SvgIcon>
        ),
        description: 'components.graphs.usage.series.imported',
      },
      {
        value: totals.confirmedCount,
        icon: (
          <SvgIcon fontSize="large" color={isTmpState ? 'action' : 'warning'}>
            <IconCheck />
          </SvgIcon>
        ),
        description: 'components.graphs.usage.series.confirmed',
      },
      {
        value: totals.exportedCount,
        icon: (
          <SvgIcon fontSize="large" color={isTmpState ? 'action' : 'success'}>
            <IconLogout />
          </SvgIcon>
        ),
        description: 'components.graphs.usage.series.exported',
      },
      {
        value: totals.rejectedCount,
        icon: (
          <SvgIcon fontSize="large" color={isTmpState ? 'action' : 'error'}>
            <IconCancel />
          </SvgIcon>
        ),
        description: 'components.graphs.usage.series.rejected',
      },
      {
        value: totals.deletedCount,
        icon: (
          <SvgIcon fontSize="large" color={isTmpState ? 'action' : 'error'}>
            <IconTrash />
          </SvgIcon>
        ),
        description: 'components.graphs.usage.series.deleted',
      },
    ],
  })
)(Aggregate);

export const TimeAggregate = injectIntl<'intl', PropsWithIntl>(
  connect((_, { isTmpState, totals, intl }: PropsWithIntl): AggregateProps => {
    const total = totals.turnaroundAvgS || 0;
    return {
      data: [
        {
          value: humanizeDuration(R.round(total, 2) * 1000, {
            largest: 2,
            language: intl.locale,
            fallbacks: [
              humanizeDurationLocaleMap[intl.locale] ?? fallbackLocale,
              fallbackLocale,
            ],
            units: limitUnits(getDurationUnit(total)),
          }),
          icon: (
            <SvgIcon fontSize="large" color={isTmpState ? 'action' : 'info'}>
              <IconCheck />
            </SvgIcon>
          ),
          description: 'components.graphs.aggregate.turnaroundAvgS',
        },
      ],
    };
  })(Aggregate)
);

export const CorrectionsAggregate = injectIntl<'intl', PropsWithIntl>(
  connect((_, { isTmpState, totals }: PropsWithIntl): AggregateProps => {
    const total = totals.correctionsPerDocumentAvg || 0;
    return {
      data: [
        {
          value: (
            <FormattedMessage
              id="components.graphs.correctionsPerDocumentAvg.unit"
              values={{ count: R.round(total, 2) }}
            />
          ),
          icon: (
            <SvgIcon fontSize="large" color={isTmpState ? 'action' : 'info'}>
              <IconPencil />
            </SvgIcon>
          ),
          description: 'components.graphs.aggregate.corrections',
        },
      ],
    };
  })(Aggregate)
);

export const TimePerDocumentAggregate = injectIntl<'intl', PropsWithIntl>(
  connect((_, { intl, isTmpState, totals }: PropsWithIntl): AggregateProps => {
    const total = totals.timePerDocumentAvgS || 0;
    return {
      data: [
        {
          value: humanizeDuration(total * 1000, {
            largest: 2,
            units: limitUnits(getDurationUnit(total)),
            language: intl.locale,
            fallbacks: [
              humanizeDurationLocaleMap[intl.locale] ?? fallbackLocale,
              fallbackLocale,
            ],
            round: true,
          }),
          icon: (
            <SvgIcon fontSize="large" color={isTmpState ? 'action' : 'info'}>
              <IconFileTime />
            </SvgIcon>
          ),
          description: 'components.graphs.aggregate.timePerDocumentAvgS',
        },
      ],
    };
  })(Aggregate)
);

export const DelayAggregate = connect(
  (_, { isTmpState, totals }: Props): AggregateProps => ({
    data: [
      {
        value: totals.exportedOnTimeCount,
        icon: (
          <SvgIcon fontSize="large" color={isTmpState ? 'action' : 'info'}>
            <IconHourglassHigh />
          </SvgIcon>
        ),
        description: 'components.graphs.delay.series.exportedOnTimeCount',
      },
      {
        value: totals.exportedLateCount,
        icon: (
          <SvgIcon fontSize="large" color={isTmpState ? 'action' : 'error'}>
            <IconHourglassEmpty />
          </SvgIcon>
        ),
        description: 'components.graphs.delay.series.exportedLateCount',
      },
    ],
  })
)(Aggregate);

export const AutomatedAggregate = connect(
  (_, { isTmpState, totals }: Props): AggregateProps => {
    return {
      data: [
        {
          value: totals.exportedAutomatedCount,
          icon: (
            <SvgIcon fontSize="medium" color={isTmpState ? 'action' : 'error'}>
              <IconBolt />
            </SvgIcon>
          ),
          description:
            'components.graphs.automated.series.exportedAutomatedCount',
        },
        {
          value: totals.exportedCount,
          icon: (
            <SvgIcon fontSize="large" color={isTmpState ? 'action' : 'info'}>
              <IconHandFinger />
            </SvgIcon>
          ),
          description:
            'components.graphs.automated.series.exportedNonAutomated',
        },
      ],
    };
  }
)(Aggregate);

export const RejectionAggregate = connect(
  (_, { isTmpState, totals }: Props): AggregateProps => ({
    data: [
      {
        value: totals.rejectedAutomaticallyCount,
        icon: (
          <SvgIcon fontSize="medium" color={isTmpState ? 'action' : 'error'}>
            <IconBolt />
          </SvgIcon>
        ),
        description: 'components.graphs.rejection.series.automatedRejections',
      },
      {
        value: totals.rejectedManuallyCount,
        icon: (
          <SvgIcon fontSize="large" color={isTmpState ? 'action' : 'info'}>
            <IconHandFinger />
          </SvgIcon>
        ),
        description: 'components.graphs.rejection.series.manualRejections',
      },
    ],
  })
)(Aggregate);
